import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MessageCircle, Send, X } from "lucide-react";
import { sendChatMessage } from "../services/ChatService";
import { toCollegeDetailsPath } from "../utility/collegeUrl";
import { useCompare } from "../context/CompareContext";

// Base gap from the viewport bottom when the compare tray isn't showing (matches the
// previous bottom-6/bottom-20 Tailwind offsets, now computed in JS so both can shift
// together above the tray).
const BASE_LAUNCHER_OFFSET = 24;
const BASE_PANEL_OFFSET = 80;

// Bump this to invalidate old sessionStorage entries (same convention as homeCollegeCache.js).
const STORAGE_KEY = "bv:ai-chat:v1:messages";
const MAX_HISTORY_SENT = 12;

const EXAMPLE_PROMPTS = [
  "MBA colleges in Mumbai under 5 lakh fees",
  "Compare IIT Delhi and IIT Bombay",
  "Top engineering colleges in Bengaluru",
];

const UNAVAILABLE_MESSAGE =
  "The AI assistant is temporarily unavailable. Please try again in a moment.";

const loadStoredMessages = () => {
  try {
    const raw = window.sessionStorage?.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const CollegeSuggestionChip = ({ college }) => (
  <Link
    to={toCollegeDetailsPath(college.collegeName, college.city, college.id)}
    className="block rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs hover:border-brand-300 hover:bg-brand-50 transition-colors"
  >
    <div className="font-medium text-slate-800 truncate">{college.collegeName}</div>
    <div className="text-slate-500 truncate">
      {[college.city, college.state].filter(Boolean).join(", ") || college.collegeCategory}
    </div>
  </Link>
);

const TypingIndicator = () => (
  <div className="mr-auto flex items-center gap-1 rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce"
        style={{ animationDelay: `${i * 0.12}s` }}
      />
    ))}
  </div>
);

const AiChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(loadStoredMessages);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const listRef = useRef(null);
  const { selected } = useCompare();
  const [trayHeight, setTrayHeight] = useState(0);

  // The compare tray (CompareTray.js, id="compare-tray") is a separate fixed-position
  // element pinned to the same bottom edge, with a height that varies by how many colleges
  // are selected and how their chips wrap on narrow screens. Without this, the launcher
  // button and open panel below sit at a fixed offset and get covered by the tray whenever
  // it's showing — most visibly on mobile, where the tray runs the full width.
  useEffect(() => {
    if (selected.length === 0) {
      setTrayHeight(0);
      return undefined;
    }
    const el = document.getElementById("compare-tray");
    if (!el) return undefined;
    const update = () => setTrayHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selected.length]);

  useEffect(() => {
    try {
      window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-40)));
    } catch {
      // ignore storage failures (private mode / quota)
    }
  }, [messages]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, isSending, isOpen]);

  const sendText = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;

    const nextMessages = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setInput("");
    setIsSending(true);

    const history = nextMessages.slice(-MAX_HISTORY_SENT - 1, -1).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      const res = await sendChatMessage({ message: trimmed, history });
      const data = res.data || {};
      if (data.status === "SUCCESS") {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.reply, colleges: data.suggestedColleges || [] },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.message || UNAVAILABLE_MESSAGE },
        ]);
      }
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: serverMessage || UNAVAILABLE_MESSAGE },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendText(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText(input);
    }
  };

  return (
    <>
      <motion.button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={isOpen ? "Close AI chat assistant" : "Open AI chat assistant"}
        style={{ bottom: BASE_LAUNCHER_OFFSET + trayHeight }}
        className="fixed right-6 z-[60] flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-white shadow-elevated hover:bg-brand-700"
      >
        {isOpen ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            style={{ bottom: BASE_PANEL_OFFSET + trayHeight }}
            className="fixed right-4 sm:right-6 z-[60] flex h-[min(56vh,calc(100dvh-8rem))] max-h-[460px] w-[80vw] max-w-xs flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-elevated"
          >
            <div className="flex items-center justify-between border-b border-slate-200 bg-brand-600 px-4 py-3 text-white">
              <div>
                <div className="text-sm font-semibold">BharatVidyarthi Assistant</div>
                <div className="text-xs text-brand-100">Ask about colleges, courses & fees</div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
                className="text-brand-100 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">
                    Hi! Ask me about colleges, courses, fees, or admissions on BharatVidyarthi.
                  </p>
                  <div className="space-y-2">
                    {EXAMPLE_PROMPTS.map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        onClick={() => sendText(prompt)}
                        className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-xs text-slate-600 hover:border-brand-300 hover:bg-brand-50"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, idx) => (
                <div key={idx} className={m.role === "user" ? "ml-auto max-w-[85%]" : "mr-auto max-w-[85%]"}>
                  <div
                    className={
                      m.role === "user"
                        ? "rounded-2xl rounded-br-sm bg-brand-600 px-4 py-2 text-sm text-white"
                        : "rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-2 text-sm text-slate-900"
                    }
                  >
                    {m.content}
                  </div>
                  {m.colleges && m.colleges.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {m.colleges.slice(0, 5).map((college) => (
                        <CollegeSuggestionChip key={college.id} college={college} />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {isSending && <TypingIndicator />}
            </div>

            <form onSubmit={handleSubmit} className="flex items-end gap-2 border-t border-slate-200 p-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about a college, course or exam..."
                disabled={isSending}
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:bg-slate-50"
              />
              <button
                type="submit"
                disabled={isSending || !input.trim()}
                aria-label="Send message"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:bg-brand-300"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AiChatWidget;
